import { useState } from "react";
import type { JSX } from "react";

import { useSourceAppConfig } from "./hooks/useSourceAppConfig";

/** The one config dialog for a source: renders exactly the credential fields the
 * source needs (from source.appconfig.list — the backend never returns stored
 * values, so secrets can't be read out of the UI), saves each via
 * source.appconfig.set, and the source then starts without a restart. Opened
 * from the source's Enable button (when it needs configuration) and from the
 * "+" add-source flow — one dialog, two entry points. */
export function ConfigureSourceDialog({
  sourceId,
  title,
  onClose,
  onSaved,
}: {
  readonly sourceId: string;
  readonly title: string;
  readonly onClose: () => void;
  readonly onSaved?: () => void;
}): JSX.Element {
  const { list, setOverride } = useSourceAppConfig();
  const entry = list.data?.sources.find((s) => s.source_id === sourceId);
  const vaultAvailable = list.data?.vault_available ?? true;
  const keys = entry?.keys ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave =
    !busy &&
    vaultAvailable &&
    keys.some((k) => (values[k.key]?.trim().length ?? 0) > 0);

  const save = (): void => {
    setBusy(true);
    setError(null);
    void (async (): Promise<void> => {
      try {
        for (const k of keys) {
          const v = values[k.key]?.trim();
          if (v !== undefined && v.length > 0) {
            await setOverride.mutateAsync({
              source_id: sourceId,
              key: k.key,
              value: v,
            });
          }
        }
        onSaved?.();
        onClose();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
        setBusy(false);
      }
    })();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md mx-4 rounded-xl bg-surface border border-edge shadow-2xl p-5">
        <h2 className="text-[15px] font-semibold text-content m-0">
          Configure {title}
        </h2>
        <p className="text-xs text-content-secondary mt-1 mb-3">
          Enter the credentials this source needs to start. Stored encrypted;
          applied without a restart.
        </p>
        {!vaultAvailable ? (
          <p className="text-xs text-amber-400 m-0 mb-3">
            The encrypted key store is not configured
            (SOURCE_SECRETS_MASTER_KEY is missing) — set the master key to save.
          </p>
        ) : null}
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save();
          }}
        >
          {keys.length === 0 ? (
            <p className="text-xs text-content-tertiary m-0">Loading…</p>
          ) : (
            keys.map((k) => (
              <label key={k.key} className="flex flex-col gap-1">
                <span className="text-xs text-content-secondary">
                  {k.label}
                  {k.deployment_configured ? " · configured" : ""}
                </span>
                <input
                  type="password"
                  value={values[k.key] ?? ""}
                  placeholder={
                    k.deployment_configured
                      ? "•••••••• (leave blank to keep)"
                      : "paste the value"
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setValues((prev) => ({ ...prev, [k.key]: v }));
                  }}
                  className="rounded-lg border border-edge bg-surface-tertiary px-3 py-2 text-sm text-content outline-none focus:border-content-muted"
                />
              </label>
            ))
          )}
          {error ? <p className="text-xs text-red-400 m-0">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded border border-edge text-content-secondary hover:text-content"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="text-xs px-3 py-1.5 rounded bg-accent text-white disabled:opacity-50 border-none cursor-pointer"
            >
              {busy ? "Saving…" : "Save & Start"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
